import { useCallback, useState, type ReactElement } from "react"
import { Loader2 } from "lucide-react"
import { toast } from "sonner"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { ApiRequestError, recordOutcome, toastApiError } from "@/lib/api"
import {
  STAGE_LABELS,
  bidLifecycleOf,
  type BidStage,
  type LifecycleAction,
} from "@/lib/bid-lifecycle"
import type { Bid } from "@/lib/schemas"
import { useBidsStore } from "@/stores/bids-store"
import { useHarvestStore } from "@/stores/harvest-store"

type ConfirmableAction = Extract<LifecycleAction, "close_no_go" | "close_withdrawn" | "reopen">

const CONFIRM_COPY: Record<ConfirmableAction, { title: string; body: string; confirm: string; done: string }> = {
  close_no_go: {
    title: "Close as no-go?",
    body: "The bid moves to Closed with the result No go. You can reopen it later.",
    confirm: "Close as no-go",
    done: "Closed as no-go",
  },
  close_withdrawn: {
    title: "Close as withdrawn?",
    body: "The bid moves to Closed with the result Withdrawn. You can reopen it later.",
    confirm: "Close as withdrawn",
    done: "Closed as withdrawn",
  },
  reopen: {
    title: "Reopen this bid?",
    body: "The bid moves back to Active, at the Submit stage. Its saved result stays on record.",
    confirm: "Reopen",
    done: "Reopened at Submit",
  },
}

/**
 * Toast a refused move. A 409 means the bid moved meanwhile (someone else, or
 * another tab): the server's message says so, and the bid is re-read so the
 * header shows where it really is.
 */
function reportMoveError(error: unknown, bidId: string, fallback: string) {
  if (error instanceof ApiRequestError && error.statusCode === 409) {
    toast.error(error.message || "The bid changed while you were moving it. Reload and try again.")
    void useBidsStore.getState().refreshBid(bidId)
    return
  }
  if (error instanceof ApiRequestError && error.statusCode === 400) {
    toast.error(error.message || fallback)
    return
  }
  toastApiError(error, fallback)
}

export interface BidLifecycleActions {
  /** Run one of the §2.7 lifecycle actions. Close-as and Reopen ask first. */
  run: (action: LifecycleAction) => void
  /** Move an active bid to another stage. */
  moveStage: (stage: BidStage) => void
  isBusy: boolean
  /** Render once, next to whatever offers the actions. */
  confirmDialog: ReactElement
}

/**
 * The lifecycle actions for one bid (plan §2.7), shared by the stage control
 * in the bid header and the row of buttons on the Submit page.
 */
export function useBidLifecycleActions(bid: Bid): BidLifecycleActions {
  const moveBidStage = useBidsStore((s) => s.moveBidStage)
  const moveBidLifecycle = useBidsStore((s) => s.moveBidLifecycle)
  const refreshBid = useBidsStore((s) => s.refreshBid)
  const requestSubmit = useHarvestStore((s) => s.requestSubmit)
  const openOutcomeDialog = useHarvestStore((s) => s.openOutcomeDialog)
  const loadHarvestStatus = useHarvestStore((s) => s.loadStatus)

  const [pending, setPending] = useState<ConfirmableAction | null>(null)
  const [isBusy, setIsBusy] = useState(false)

  const moveStage = useCallback(
    (stage: BidStage) => {
      const current = bidLifecycleOf(bid)
      if (current.lifecycle !== "active" || current.stage === stage) return
      setIsBusy(true)
      moveBidStage(bid.id, stage)
        .then(() => toast.success(`Stage: ${STAGE_LABELS[stage]}`))
        .catch((error: unknown) => reportMoveError(error, bid.id, "Could not change the stage"))
        .finally(() => setIsBusy(false))
    },
    [bid, moveBidStage]
  )

  const runConfirmed = async (action: ConfirmableAction) => {
    setIsBusy(true)
    try {
      if (action === "reopen") {
        await moveBidLifecycle(bid.id, { lifecycle: "active", stage: "submit" })
      } else {
        // `_outcome` closes the bid in the same transaction (R4 T2).
        await recordOutcome(bid.id, { outcome: action === "close_no_go" ? "no_go" : "withdrawn" })
        await refreshBid(bid.id)
        void loadHarvestStatus(bid.id, { force: true })
      }
      toast.success(CONFIRM_COPY[action].done)
      setPending(null)
    } catch (error) {
      setPending(null)
      reportMoveError(error, bid.id, "Could not move the bid")
    } finally {
      setIsBusy(false)
    }
  }

  const run = useCallback(
    (action: LifecycleAction) => {
      switch (action) {
        case "close_no_go":
        case "close_withdrawn":
        case "reopen":
          setPending(action)
          return
        case "record_result":
        case "edit_result":
          openOutcomeDialog({ id: bid.id, title: bid.title })
          return
        case "mark_submitted":
          // Today's flow, unchanged: the harvest dialog when there is something
          // to harvest, otherwise a plain move to Submitted.
          requestSubmit({ id: bid.id, title: bid.title })
            .then(() => {
              if (!useHarvestStore.getState().submitDialog) toast.success("Marked as submitted")
            })
            .catch((error: unknown) => reportMoveError(error, bid.id, "Could not mark the bid as submitted"))
          return
        case "start_bidding":
          setIsBusy(true)
          moveBidLifecycle(bid.id, { lifecycle: "active", stage: "qualify" })
            .then(() => toast.success("Bidding started at Qualify"))
            .catch((error: unknown) => reportMoveError(error, bid.id, "Could not start bidding"))
            .finally(() => setIsBusy(false))
          return
        case "move_to_leads":
          setIsBusy(true)
          moveBidLifecycle(bid.id, { lifecycle: "lead" })
            .then(() => toast.success("Moved to Leads"))
            .catch((error: unknown) => reportMoveError(error, bid.id, "Could not move the bid to Leads"))
            .finally(() => setIsBusy(false))
          return
      }
    },
    [bid, moveBidLifecycle, openOutcomeDialog, requestSubmit]
  )

  const copy = pending ? CONFIRM_COPY[pending] : null

  const confirmDialog = (
    <AlertDialog open={pending !== null} onOpenChange={(open) => !open && !isBusy && setPending(null)}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{copy?.title}</AlertDialogTitle>
          <AlertDialogDescription>{copy?.body}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isBusy}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={isBusy}
            onClick={(e) => {
              e.preventDefault()
              if (pending) void runConfirmed(pending)
            }}
          >
            {isBusy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {copy?.confirm}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )

  return { run, moveStage, isBusy, confirmDialog }
}
